
import { defineComponent, ref, reactive, toRefs, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { createModel, createModelArray } from '@/utils/component'
import { getEmployeeById, updateEmployee } from '@/apis/main-data/employee'
import Container from '@/components/form/container/Container.vue'
import DynamicForm from '@/components/form/common/DynamicForm.vue'
import { EmployeeInfoModel, UpdateEmployeeModel } from '@/model/master-data/employeeModel'
import { ElForm, ElMessage } from 'element-plus'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'

export default defineComponent({
  components: {
    DynamicForm,
    Container
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const updateFormRef = ref(ElForm)
    const id = useRoute().params.id.toString()
    const isDataLoaded = ref(false)
    const employeeStruct = [
      { prop: 'nameCn', label: t('employee.nameCn'), component: 'el-input', type: 'text', model: '' },
      { prop: 'nameEn', label: t('employee.nameEn'), component: 'el-input', type: 'text', model: '' },
      { prop: 'employeeNumber', label: t('employee.employeeId'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'companyId',
        label: t('employee.employingCompany'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'mail', label: t('employee.email'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'isDelete',
        label: t('common.deleteStatus'),
        component: 'el-radio-group',
        type: 'boolean',
        model: false,
        options: [
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      },
      { prop: 'employeeBanks', label: t('bank.bankInfo'), component: 'custom-groups' }
    ]
    const emailVerify = (rule: any, value: any, callback: any) => {
      const reg = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
      if (value && !reg.test(value)) {
        callback(new Error(t('employee.emailIsError')))
      } else {
        callback()
      }
    }
    const employeeRules = {
      nameCn: [{ required: true, message: t('employee.nameCnP'), trigger: 'blur' }],
      nameEn: [{ required: true, message: t('employee.nameEnP'), trigger: 'blur' }],
      employeeNumber: [{ required: true, message: t('employee.employeeIdP'), trigger: 'blur' }],
      companyId: [{ required: true, message: t('employee.employingCompanyP'), trigger: 'blur' }],
      mail: [{ validator: emailVerify, trigger: 'blur' }]
    }
    const bankStruct = [
      { prop: 'bankAccount', label: t('bank.bankAccount'), component: 'el-input', type: 'text', model: '' },
      { prop: 'bankCode', label: t('bank.bankCode'), component: 'el-input', type: 'text', model: '' },
      { prop: 'bankName', label: t('bank.bankName'), component: 'el-input', type: 'text', model: '' },
      { prop: 'bankCity', label: t('bank.bankCity'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'isCompanyAccount',
        label: t('order.isCompanyAccount'),
        component: 'el-radio-group',
        type: 'boolean',
        model: null,
        options: [
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      }
    ]
    const bankRules = {
      bankAccount: [{ required: true, message: t('bank.bankAccountP'), trigger: 'blur' }],
      bankCode: [{ required: true, message: t('bank.bankCodeP'), trigger: 'blur' }],
      bankName: [{ required: true, message: t('bank.bankNameP'), trigger: 'blur' }],
      bankCity: [{ required: true, message: t('bank.bankCityP'), trigger: 'blur' }],
      isCompanyAccount: [{ required: true, message: t('order.isCompanyAccountP'), trigger: 'change' }]
    }
    const dataMap = reactive({
      formStruct: employeeStruct,
      formModel: createModel(employeeStruct) as EmployeeInfoModel,
      groupFormStruct: bankStruct,
      groupFormModels: createModelArray(bankStruct),
      async getEmployeeInfo() {
        let employeeId
        try {
          employeeId = Number(id)
        } catch (error) {
          ElMessage({
            message: 'id 格式错误;' + id,
            type: 'error',
            duration: 2000
          })
          return
        }
        const data = await getEmployeeById(employeeId)
        const employeeInfo = data?.data ?? null
        if (employeeInfo) {
          dataMap.formModel.id = employeeInfo.id
          dataMap.formModel.companyCode = employeeInfo.companyCode
          dataMap.formModel.companyId = employeeInfo.companyId
          dataMap.formModel.companyName = employeeInfo.companyName
          dataMap.formModel.employeeNumber = employeeInfo.employeeNumber
          dataMap.formModel.isDelete = employeeInfo.isDelete
          dataMap.formModel.mail = employeeInfo.mail
          dataMap.formModel.nameCn = employeeInfo.nameCn
          dataMap.formModel.nameEn = employeeInfo.nameEn
          dataMap.groupFormModels = [{
            bankAccount: employeeInfo.bankAccount,
            bankCode: employeeInfo.bankCode,
            bankName: employeeInfo.bankName,
            bankCity: employeeInfo.bankCity,
            isCompanyAccount: employeeInfo.isCompanyAccount
          }]
          isDataLoaded.value = true
        }
      },
      async toSubmit(submitData: any) {
        const params: UpdateEmployeeModel = {
          id: submitData.id,
          companyId: submitData.companyId,
          employeeNumber: submitData.employeeNumber,
          isDelete: submitData.isDelete,
          mail: submitData.mail,
          nameCn: submitData.nameCn,
          nameEn: submitData.nameEn,
          bankAccount: submitData.model[0].bankAccount,
          bankCode: submitData.model[0].bankCode,
          bankName: submitData.model[0].bankName,
          bankCity: submitData.model[0].bankCity,
          isCompanyAccount: submitData.model[0].isCompanyAccount
        }
        const res = await updateEmployee(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
          await router.push({ name: 'EmployeePageList', state: { refresh: true } })
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const getCompanyList = async() => {
      dataMap.formStruct[3].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({ label: iterator.name, value: iterator.id })
      }
      dataMap.formStruct[3].options = optionList
      dataMap.formStruct[3].loading = false
    }
    onMounted(() => {
      getCompanyList()
      dataMap.getEmployeeInfo()
    })
    return {
      t,
      updateFormRef,
      employeeRules,
      bankRules,
      isDataLoaded,
      ...toRefs(dataMap)
    }
  }
})
